import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, ChevronDown } from "lucide-react";
import { api, errMsg, fmtDT, DAYS } from "../../lib/api";
import { Btn, Card, Empty, Field, Input } from "../../components/kit";

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
const Pill = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`h-8 px-2.5 rounded-full border text-xs ${on ? "border-[#FF6B00] text-[#FF6B00] bg-[#FF6B00]/10" : "border-[#2a2a2a] text-neutral-500"}`}>{children}</button>
);
const ST = { pending: "text-amber-400", approved: "text-emerald-400", rejected: "text-red-400", suspended: "text-red-400" };

function SpecCard({ s, services, onSaved }) {
  const [open, setOpen] = useState(s.status === "pending");
  const [d, setD] = useState({
    categories: s.categories?.length ? s.categories : s.requested_categories || [], approved_services: s.approved_services?.length ? s.approved_services : s.requested_services || [],
    radius_miles: s.radius_miles, skills: s.skills, tools: s.tools, availability: { days: [], start: "08:00", end: "18:00", ...(s.availability || {}) },
  });
  const [busy, setBusy] = useState("");
  const save = async (status) => {
    setBusy(status || "save");
    try {
      await api.put(`/admin/specialists/${s.id}`, { ...d, radius_miles: Number(d.radius_miles), ...(status ? { status } : {}) });
      toast.success(status ? `Specialist ${status}` : "Saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const av = d.availability;
  return (
    <Card className="space-y-3" data-testid="specialist-card">
      <button className="w-full flex items-center justify-between text-left" onClick={() => setOpen(!open)} data-testid="specialist-card-toggle">
        <div>
          <div className="font-medium">{s.name} {s.is_demo && <span className="text-[10px] font-mono text-purple-300 border border-purple-800 rounded px-1">DEMO</span>}</div>
          <div className="text-xs text-neutral-500">{s.email} · {s.phone}</div>
          <div className="text-xs mt-0.5"><span className={ST[s.status]} data-testid="specialist-status">{s.status}</span> · {s.available ? <span className="text-emerald-400">Available</span> : "Offline"}{s.location && ` · loc ${fmtDT(s.location.updated_at)}`}</div>
        </div>
        <ChevronDown className={`w-4 h-4 text-neutral-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-3 border-t border-[#1f1f1f] pt-3">
          <div className="text-xs text-neutral-500">Requested: {(s.requested_categories || []).join(", ")} · Start ZIP {s.start_zip} {s.start_address}</div>
          <Field label="Approved categories">
            <div className="flex gap-2">{["mechanical", "detailing"].map((c) => <Pill key={c} testid={`spec-cat-${c}`} on={d.categories.includes(c)} onClick={() => setD({ ...d, categories: toggle(d.categories, c) })}>{c}</Pill>)}</div>
          </Field>
          <Field label="Approved services (equipment-dependent marked *)">
            <div className="flex flex-wrap gap-1.5">
              {services.filter((x) => d.categories.includes(x.category)).map((x) => (
                <Pill key={x.key} testid={`spec-svc-${x.key}`} on={d.approved_services.includes(x.key)} onClick={() => setD({ ...d, approved_services: toggle(d.approved_services, x.key) })}>{x.name}{x.requires_equipment ? " *" : ""}</Pill>
              ))}
            </div>
          </Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Radius mi"><Input type="number" value={d.radius_miles} onChange={(e) => setD({ ...d, radius_miles: e.target.value })} data-testid="spec-radius" /></Field>
            <Field label="From"><Input type="time" value={av.start} onChange={(e) => setD({ ...d, availability: { ...av, start: e.target.value } })} /></Field>
            <Field label="To"><Input type="time" value={av.end} onChange={(e) => setD({ ...d, availability: { ...av, end: e.target.value } })} /></Field>
          </div>
          <div className="flex flex-wrap gap-1.5">{DAYS.map((x, i) => <Pill key={x} on={av.days.includes(i)} onClick={() => setD({ ...d, availability: { ...av, days: toggle(av.days, i) } })}>{x}</Pill>)}</div>
          <Field label="Skills"><Input value={d.skills} onChange={(e) => setD({ ...d, skills: e.target.value })} /></Field>
          <Field label="Tools / equipment"><Input value={d.tools} onChange={(e) => setD({ ...d, tools: e.target.value })} /></Field>
          <div className="grid grid-cols-3 gap-2">
            <Btn variant="success" size="sm" loading={busy === "approved"} onClick={() => save("approved")} data-testid="spec-approve-btn">Approve</Btn>
            <Btn variant="outline" size="sm" loading={busy === "save"} onClick={() => save()} data-testid="spec-save-btn">Save</Btn>
            <Btn variant="danger" size="sm" loading={busy === "suspended"} onClick={() => save(s.status === "pending" ? "rejected" : "suspended")} data-testid="spec-suspend-btn">{s.status === "pending" ? "Reject" : "Suspend"}</Btn>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function Specialists() {
  const [list, setList] = useState(null);
  const [services, setServices] = useState([]);
  const load = () => api.get("/admin/specialists").then(({ data }) => setList(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
  }, []);
  return (
    <div className="space-y-3 fade-in">
      <h1 className="font-head text-3xl uppercase font-bold">Team</h1>
      {list && !list.length && <Empty icon={Users} title="No specialists yet" text="Share the Apply link from the sign-in page." />}
      {(list || []).map((s) => <SpecCard key={s.id + s.status} s={s} services={services} onSaved={load} />)}
    </div>
  );
}
